import React, { useRef } from 'react';

import { Icon } from 'components/Icon';
import { useOnClickOutside } from 'hooks/useOnClickOutside';

import {
  CartPillCount,
  CartPillDetails,
  CartPillLink,
  CartPillListItemCount,
  CartPillListItemLi,
  CartPillListItemLink,
  CartPillListItemOrgName,
  CartPillListUl,
  CartPillNoLink,
  CartPillSummary,
} from './CartPill.styled';
import { CartPillOrgData } from './CartPillOrgData';

const pageHeaderCartPillQaId = 'cart-pill';
const pageHeaderCartPillCountQaId = 'cart-pill-count';

type Props = {
  selectedOrgId: string | undefined;
  getOrgCartDataHref: (orgCartData: CartPillOrgData) => string;
  orgsCartData: CartPillOrgData[];
};

export function CartPill({
  selectedOrgId,
  getOrgCartDataHref,
  orgsCartData,
}: Props) {
  const containerRef = useRef<HTMLDetailsElement>(null);
  useOnClickOutside({
    containerRef,
    onClickOutside: () => {
      containerRef.current?.removeAttribute('open');
    },
  });

  const totalItemsCount = orgsCartData.reduce(
    (total, data) => total + data.itemsCount,
    0,
  );

  const filteredOrgsCartData = orgsCartData.filter(
    (orgCartData) => orgCartData.itemsCount > 0,
  );

  const countAndIcon = (
    <>
      <CartPillCount data-qa-id={pageHeaderCartPillCountQaId}>
        {totalItemsCount}
      </CartPillCount>

      <Icon name="cart" size={18} initialColorName="white" />
    </>
  );

  if (orgsCartData.length === 1) {
    return (
      <CartPillLink
        to={getOrgCartDataHref(orgsCartData[0])}
        data-qa-id={pageHeaderCartPillQaId}
      >
        {countAndIcon}
      </CartPillLink>
    );
  }

  if (filteredOrgsCartData.length === 0) {
    return (
      <CartPillNoLink data-qa-id={pageHeaderCartPillQaId}>
        {countAndIcon}
      </CartPillNoLink>
    );
  }

  return (
    <CartPillDetails
      ref={containerRef}
      role="list"
      data-qa-id={pageHeaderCartPillQaId}
    >
      <CartPillSummary>
        {countAndIcon}

        <Icon name="chevron-down" size={9} initialColorName="white" />
      </CartPillSummary>

      <CartPillListUl>
        {filteredOrgsCartData.map((orgCartData) => (
          <CartPillListItemLi
            key={orgCartData.id}
            data-selected={orgCartData.id === selectedOrgId}
          >
            <CartPillListItemLink
              to={getOrgCartDataHref(orgCartData)}
              $selected={orgCartData.id === selectedOrgId}
            >
              <CartPillListItemCount data-qa-id="cart-pill-list-item-count">
                {orgCartData.itemsCount}
              </CartPillListItemCount>

              <CartPillListItemOrgName data-qa-id="cart-pill-list-item-org-name">
                {orgCartData.name}
              </CartPillListItemOrgName>
            </CartPillListItemLink>
          </CartPillListItemLi>
        ))}
      </CartPillListUl>
    </CartPillDetails>
  );
}
